// web/src/routes/auth/Signup.jsx
// Matches the Login split layout: horse image left (public/horse.jpg),
// forest-green panel right with the lockup. Responsive (image → top banner
// on mobile). Signup posts { name, email, password }; password must be >= 10.

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useSignup } from '../../lib/auth';
import Field, { UserIcon, MailIcon, LockIcon } from '../../components/ui/Field.jsx';
import lockupUrl from '../../assets/stallside-lockup-white.png';

export default function Signup() {
  const { user } = useAuth();
  const signup = useSignup();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // After a successful signup the backend sets a session, so /me populates and
  // we leave for the dashboard (which shows barn onboarding for a new account).
  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const canSubmit = email.trim() && password.length >= 10 && !signup.isPending;

  function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    signup.mutate({ name: name.trim() || undefined, email: email.trim(), password });
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#1a2b1f]">
      {/* Image — top banner on mobile, left column on desktop */}
      <div className="relative h-48 sm:h-64 md:h-auto md:flex-1">
        <img src="/horse.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-transparent to-[#1a2b1f]/90 md:to-[#1a2b1f]" />
        <div className="hidden md:block absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/55 to-transparent" />
      </div>

      {/* Sign-up panel */}
      <div className="w-full md:w-[440px] lg:w-[480px] flex-1 md:flex-none flex flex-col text-[#f5f1e8]">
        <div className="flex-1 flex flex-col justify-center w-full max-w-sm mx-auto px-8 sm:px-12 py-10">
          <img src={lockupUrl} alt="stallSIDE" className="w-full h-auto mb-8" />

          <form onSubmit={handleSubmit} className="space-y-3">
            <Field
              icon={<UserIcon />}
              type="text"
              placeholder="Name"
              autoComplete="name"
              value={name}
              onChange={setName}
            />
            <Field
              icon={<MailIcon />}
              type="email"
              placeholder="Email"
              autoComplete="email"
              value={email}
              onChange={setEmail}
            />
            <div>
              <Field
                icon={<LockIcon />}
                type="password"
                placeholder="Password"
                autoComplete="new-password"
                value={password}
                onChange={setPassword}
                reveal
              />
              <p
                className={`mt-1 text-xs ${
                  password && password.length < 10 ? 'text-amber-300' : 'text-[#f5f1e8]/50'
                }`}
              >
                At least 10 characters.
              </p>
            </div>

            {signup.isError && (
              <p className="text-sm text-red-300">{friendlyError(signup.error)}</p>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full py-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold uppercase tracking-wide transition disabled:opacity-60"
            >
              {signup.isPending ? 'Creating account…' : 'Sign Up'}
            </button>
          </form>

          <p className="mt-6 text-sm text-center text-[#f5f1e8]/60">
            Already have an account?{' '}
            <Link to="/login" className="text-[#f5f1e8] underline hover:text-white">
              Log in
            </Link>
          </p>
        </div>

        <footer className="px-8 sm:px-12 py-6 text-xs text-[#f5f1e8]/40 text-center">
          © {new Date().getFullYear()} stallSIDE. All rights reserved.
        </footer>
      </div>
    </div>
  );
}

function friendlyError(err) {
  const code = err?.body?.error || err?.message || '';
  switch (code) {
    case 'email_taken':
      return 'That email is already registered. Try logging in.';
    case 'password_too_short':
      return 'Password must be at least 10 characters.';
    case 'missing_fields':
      return 'Email and password are required.';
    default:
      return 'Couldn’t create your account. Try again.';
  }
}
